import PropTypes from 'prop-types';
import React, { useState } from 'react';

import ErrorModal from 'src/enosikit/components/ErrorModal';

/**
 * Surface-agnostic error UI shell. Renders a caller-supplied skeleton in the
 * background and layers a blocking retry/cancel modal above it. Contains only
 * display logic; callers compose retry and reset behaviour via the
 * `onCancelFunc` / `onRetryFunc` callbacks and choose the background skeleton
 * via `placeholderComponent`.
 * @param {object} props - Component props.
 * @param {string|null} props.bodyText - Optional custom modal body message.
 *   When null, ErrorModal renders its default body.
 * @param {object|null} props.details - Optional diagnostic payload rendered in
 *   the modal's "Show Details" section. When null, the section is hidden.
 * @param {Function|null} props.onCancelFunc - Invoked when the user dismisses
 *   the modal. The modal closes itself regardless of the callback's return.
 * @param {Function|null} props.onRetryFunc - Invoked when the user clicks
 *   retry. When null, the retry action is hidden by ErrorModal.
 * @param {React.ComponentType} props.placeholderComponent - Skeleton component
 *   rendered behind the modal for the surface being protected.
 * @param {boolean} props.retryDisabled - Disables retry when retry cannot
 *   succeed (for example, while a replay is in flight).
 * @returns {React.ReactNode} placeholder error UI.
 */
function PlaceholderErrorModal({
  bodyText, details, onCancelFunc, onRetryFunc,
  placeholderComponent: PlaceholderComponent, retryDisabled,
}) {
  const [isModalOpen, setIsModalOpen] = useState(true);

  const handleCancelFunc = () => {
    onCancelFunc?.();
    setIsModalOpen(false);
  };

  return (
    <>
      <PlaceholderComponent />
      {isModalOpen && (
        <ErrorModal
          bodyText={bodyText}
          details={details}
          onCancelFunc={handleCancelFunc}
          onRetryFunc={onRetryFunc}
          retryDisabled={retryDisabled}
        />
      )}
    </>
  );
}

PlaceholderErrorModal.propTypes = {
  bodyText: PropTypes.string,
  details: PropTypes.shape({
    componentStack: PropTypes.string,
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  onCancelFunc: PropTypes.func,
  onRetryFunc: PropTypes.func,
  placeholderComponent: PropTypes.elementType.isRequired,
  retryDisabled: PropTypes.bool,
};

PlaceholderErrorModal.defaultProps = {
  bodyText: null,
  details: null,
  onCancelFunc: null,
  onRetryFunc: null,
  retryDisabled: false,
};

export default PlaceholderErrorModal;
